import React, { useEffect } from "react";
import Autocomplete, { AutocompleteProps } from "@material-ui/lab/Autocomplete";
import { useField } from "formik";
import cssModuleClassNames from "classnames/bind";
import {
  FormLabel,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MaterialSelect,
  TextField,
  SelectProps as MaterialSelectProps,
  Radio,
} from "@material-ui/core";
import inputStyles from "../inputs/input.module.css";
export { MultiSelect } from "./multi";
const inputClassNames = cssModuleClassNames.bind(inputStyles);

type HtmlSelectProps = React.DetailedHTMLProps<
React.SelectHTMLAttributes<HTMLSelectElement>,
HTMLSelectElement
>;

export type SelectProps = HtmlSelectProps;


export type MaterialFormikSelectProps = {options: any[], name: string, margin?: "normal" | "dense" | "none"} & SelectProps
// eslint-disable-next-line no-warning-comments
// FIXME: unify with material.tsx select
export const MaterialFormikSelect: React.FC<MaterialFormikSelectProps> = (props) => {
  const { options, onChange: handleChange, ...inputProps } = props;
  const { name } = props;
  const [field, meta, helpers] = useField(inputProps);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);
  return (
    <FormControl fullWidth variant="outlined" margin={props.margin || "normal"}
      required={props.required}
      disabled={props.disabled}
      error={meta.touched && !!meta.error}
    >
      <InputLabel id={`${name}-label`}>{props.title}</InputLabel>
      <MaterialSelect label={props.title}
        labelId={`${name}-label`}
        {...field}
        onChange={async (e) => {
          if (props.onBlur) props.onBlur(e as any);
          if (handleChange) await handleChange(e as any);
          return field.onChange(e);
        }}
      >
        {options.map(({ label, value }: { label: string, value: string }) => (
          <MenuItem key={`${name}-${value}`} value={value}>{label}</MenuItem>
        ))}
      </MaterialSelect>
      {meta.touched && meta.error && (
        <FormHelperText>
          {meta.error}
        </FormHelperText>
      )}
    </FormControl>);
};
type FormikAutocompleteProps = {
  options: any[],
  name: string,
  margin?: "normal" | "dense"| "none"
  value?: string | number | readonly string[]
  defaultValue?: string | number | readonly string[]
} & Omit<MaterialSelectProps, "ref" | "rows" | "onChange" | "innerRef">
export const MaterialFormikSelectAutocomplete: React.FC<FormikAutocompleteProps> = (props) => {
  const { options, fullWidth, ...inputProps } = props;
  const { name } = props;
  const [field, meta, helpers] = useField(inputProps);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);
  const optionsMap = options.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
  return (
    <Autocomplete
      id={name}
      fullWidth={fullWidth}
      {...field}
      disabled={props.disabled}
      onChange={(_e, value) => {
        if (props.onBlur) props.onBlur({ target: { value } } as any);
        return helpers.setValue(value || "");
      }}
      options={props.options.map(({ value }) => value)}
      getOptionLabel={(value) => optionsMap[value] || ""}
      renderInput={(params) => <TextField
        {...params}
        label={props.title || props.label}
        margin={props.margin || "normal"} variant="outlined" required={props.required}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
      />}
    />);
};


/** Normal select field that can be used outside of Formik
 *
 *  Example:
 *  ```
 *  <Select name="foo">
 *    <option value="bar">
 *    <option value="baz">
 *  </Select>
 *  ```
 */
export const Select: React.FC<SelectProps> = (props) => {
  const { className, ...inputProps } = props;
  return <select className={inputClassNames(className, inputStyles.input)} {...inputProps} />;
};

// eslint-disable-next-line max-len
export type SelectAutocompleteProps<T, U extends boolean | undefined = undefined, V extends boolean | undefined = undefined, W extends boolean | undefined = undefined> = {
  margin?: "dense" | "none" | "normal";
  label?: string;
  error?: boolean;
  required?: boolean;
  helperText?: string;
} & Omit<AutocompleteProps<T, U, V, W>, "renderInput">;

// eslint-disable-next-line func-style
export function SelectAutocomplete<T, Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined> (
  props: SelectAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
): JSX.Element {
  const { margin, label, helperText, error, required, ...autocompleteProps } = props;
  return (
    <Autocomplete
      renderInput={(params) => (<TextField
        {...params}
        required={required}
        error={error}
        helperText={helperText}
        margin={margin ?? "normal"}
        label={label ?? "Select one:"}
        variant="outlined"
      />)}
      {...autocompleteProps}
    />
  );
}

interface RadioOption {
  label: string;
  value: string;
}

interface RadioSelectComponentProps {
  name: string;
  label: string;
  options: RadioOption[];
}

interface RadioOption {
  label: string;
  value: string;
}

interface RadioSelectComponentProps {
  name: string;
  label: string;
  options: RadioOption[];
}

export const RadioSelectComponent: React.FC<RadioSelectComponentProps> =
({ name, label, options }) => {
  const [field, meta] = useField(name);

  return (
    <FormControl component="fieldset" error={meta.touched && Boolean(meta.error)} variant="outlined">
      <FormLabel component="legend">{label}</FormLabel>
      <RadioGroup {...field} aria-label={name} name={name}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
      {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  );
};
